// src/components/admin/RepManagement.js
import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { COMMISSION_TYPES } from '../../config/constants';
import { Users, Plus, Edit2, Search, Loader2, ChevronDown, ChevronUp, DollarSign, UserCheck, UserX, Mail } from 'lucide-react';

export function RepManagement({ reps, loading }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [expandedRep, setExpandedRep] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    companyName: '',
    commissionType: 'percentage',
    commissionRate: '10',
    notes: ''
  });

  const filteredReps = reps.filter(rep =>
    rep.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rep.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rep.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      companyName: '',
      commissionType: 'percentage',
      commissionRate: '10',
      notes: ''
    });
    setShowAddForm(false);
    setEditingRep(null);
    setError('');
  };

  const handleEdit = (rep) => {
    setFormData({
      email: rep.email || '',
      password: '',
      name: rep.name || '',
      phone: rep.phone || '',
      companyName: rep.companyName || '',
      commissionType: rep.commissionType || 'percentage',
      commissionRate: rep.commissionRate?.toString() || '10',
      notes: rep.notes || ''
    });
    setEditingRep(rep.id);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingRep) {
        // Update existing rep
        await updateDoc(doc(db, 'users', editingRep), {
          name: formData.name,
          phone: formData.phone,
          companyName: formData.companyName,
          commissionType: formData.commissionType,
          commissionRate: parseFloat(formData.commissionRate) || 0,
          notes: formData.notes,
          updatedAt: Timestamp.now()
        });
      } else {
        // Create new rep server-side (Admin SDK) so this admin session
        // isn't replaced by the new rep's login
        const createRep = httpsCallable(functions, 'createRep');
        await createRep({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone,
          companyName: formData.companyName,
          commissionType: formData.commissionType,
          commissionRate: parseFloat(formData.commissionRate) || 0,
          notes: formData.notes
        });
      }

      resetForm();
    } catch (err) {
      console.error('Error saving rep:', err);
      if (err.code === 'auth/email-already-in-use' || err.code === 'functions/already-exists') {
        setError('An account with this email already exists');
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rep) => {
    try {
      await updateDoc(doc(db, 'users', rep.id), {
        active: !rep.active,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error toggling rep status:', err);
    }
  };

  const handleApprove = async (rep) => {
    try {
      await updateDoc(doc(db, 'users', rep.id), {
        approved: true,
        approvedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error approving rep:', err);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto" size={32} />
        <p className="text-gray-500 mt-4">Loading reps...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Sales Rep Management</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2"
          >
            <Plus size={18} /> Add Rep
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingRep ? 'Edit Rep' : 'Add New Rep'}
          </h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!editingRep && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Password *</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Commission Type</label>
                <select
                  value={formData.commissionType}
                  onChange={(e) => setFormData({ ...formData, commissionType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Object.values(COMMISSION_TYPES).map(type => (
                    <option key={type.key} value={type.key}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Commission Rate {formData.commissionType === 'percentage' ? '(%)' : '($ per unit)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.commissionRate}
                  onChange={(e) => setFormData({ ...formData, commissionRate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingRep ? 'Update Rep' : 'Create Rep'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search reps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Reps List */}
      {filteredReps.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {searchTerm ? 'No reps found matching your search' : 'No reps yet. Add your first rep above.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReps.map(rep => {
            const isExpanded = expandedRep === rep.id;
            return (
              <div key={rep.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRep(isExpanded ? null : rep.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${rep.active !== false ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Users size={20} className={rep.active !== false ? 'text-green-600' : 'text-gray-400'} />
                    </div>
                    <div>
                      <p className="font-semibold">{rep.name || 'Unnamed Rep'}</p>
                      <p className="text-sm text-gray-500">{rep.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right mr-4">
                      <p className="text-sm font-medium">
                        {rep.commissionType === 'percentage' 
                          ? `${rep.commissionRate}% commission`
                          : `$${rep.commissionRate}/unit`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {rep.customerCount || 0} customers
                      </p>
                    </div>
                    
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      !rep.approved
                        ? 'bg-yellow-100 text-yellow-800'
                        : rep.active !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {!rep.approved ? 'Pending Approval' : rep.active !== false ? 'Active' : 'Inactive'}
                    </span>

                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Details */}
                      <div>
                        <h4 className="font-semibold mb-2">Contact Info</h4>
                        <p className="text-sm"><strong>Phone:</strong> {rep.phone || '-'}</p>
                        <p className="text-sm"><strong>Company:</strong> {rep.companyName || '-'}</p>
                        {rep.notes && (
                          <p className="text-sm mt-2"><strong>Notes:</strong> {rep.notes}</p>
                        )}
                      </div>

                      {/* Commission Stats */}
                      <div>
                        <h4 className="font-semibold mb-2">Commission</h4>
                        <div className="space-y-1">
                          <p className="text-sm flex justify-between">
                            <span>Total Earned:</span>
                            <span className="font-medium">${(rep.totalCommissionEarned || 0).toFixed(2)}</span>
                          </p>
                          <p className="text-sm flex justify-between">
                            <span>Total Paid:</span>
                            <span className="font-medium">${(rep.totalCommissionPaid || 0).toFixed(2)}</span>
                          </p>
                          <p className="text-sm flex justify-between border-t pt-1">
                            <span>Owed:</span>
                            <span className="font-bold text-green-600">
                              ${((rep.totalCommissionEarned || 0) - (rep.totalCommissionPaid || 0)).toFixed(2)}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div>
                        <h4 className="font-semibold mb-2">Actions</h4>
                        <div className="space-y-2">
                          {!rep.approved && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(rep); }}
                              className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center justify-center gap-2"
                            >
                              <UserCheck size={14} /> Approve Rep
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(rep); }}
                            className="w-full px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm flex items-center justify-center gap-2"
                          >
                            <Edit2 size={14} /> Edit Rep
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleActive(rep); }}
                            className={`w-full px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 ${
                              rep.active !== false
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                          >
                            {rep.active !== false ? <UserX size={14} /> : <UserCheck size={14} />}
                            {rep.active !== false ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
